import { useCallback, useEffect, useRef, useState } from "react";

/** 左右どちらかが潰れて使えなくならないよう、取れる範囲を決めておく。 */
const MIN_RATIO = 0.2;
const MAX_RATIO = 0.8;

const storageKeyOf = (key: string) => `split-ratio:${key}`;

const clamp = (ratio: number) => Math.min(Math.max(ratio, MIN_RATIO), MAX_RATIO);

function load(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(storageKeyOf(key));
    const value = raw == null ? NaN : Number(raw);
    return clamp(Number.isFinite(value) ? value : fallback);
  } catch {
    // プライベートモード等での失敗は既定値で続行する。
    return clamp(fallback);
  }
}

/**
 * 左右に割った画面の比率を、境目のドラッグで変えられるようにする。
 *
 * 幅ではなく**比率**で持つ。px で持つとウィンドウを広げたときに片側だけが
 * 広がり、使う人が決めた割合が崩れるため。既定で右をやや広くしているのは、
 * 図面は読めれば足りるのに対し、工程の表は列が多く横に広いため。比率は画面ごとに localStorage へ
 * 保存する（図面を大きく見たいか、工程の表を広く使いたいかは人による）。
 *
 * ドラッグ中は `isResizing` が true になる。呼び出し側はこの間、ペインの中身
 * （iframe など）の `pointer-events` を切ること。iframe の上にカーソルが入ると
 * mousemove が iframe 側に吸われ、ドラッグが途切れるため。
 */
export function useResizableSplit(storageKey: string, defaultRatio = 0.45) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState(() => load(storageKey, defaultRatio));
  const [isResizing, setIsResizing] = useState(false);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const onMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      if (rect.width === 0) return;
      setRatio(clamp((e.clientX - rect.left) / rect.width));
    };
    const onUp = () => setIsResizing(false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    // ドラッグ中はカーソルを保ち、テキスト選択が走らないようにする。
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
    };
  }, [isResizing]);

  // ドラッグが終わったときだけ保存する（mousemove のたびに書かない）。
  useEffect(() => {
    if (isResizing) return;
    try {
      localStorage.setItem(storageKeyOf(storageKey), String(ratio));
    } catch {
      // 保存に失敗しても操作は続けられるようにする。
    }
  }, [isResizing, storageKey, ratio]);

  return { containerRef, ratio, isResizing, startResize };
}
